// The admin console's routes. Everything but sign-in needs a staff session with MFA: the
// authed layout loads /v1/admin/me and sends anyone else to sign-in, which says why.
import type { QueryClient } from '@tanstack/react-query'
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router'
import { isApiError, safeReturnTo } from './api/client'
import { meQuery } from './api/queries'
import { RootLayout } from './components/RootLayout'
import { Shell } from './components/Shell'
import { AbusePage } from './routes/Abuse'
import { AuditPage } from './routes/Audit'
import { CostModelPage } from './routes/CostModel'
import { ERASURE_FILTERS, type ErasureFilter, ErasuresPage } from './routes/Erasures'
import { HealthPage } from './routes/Health'
import { InfraPage } from './routes/Infra'
import { MarginPage } from './routes/Margin'
import { NfsePage } from './routes/Nfse'
import { NotFoundPage } from './routes/NotFound'
import { OrganizationPage } from './routes/Organization'
import { OrganizationsPage } from './routes/Organizations'
import { OverviewPage } from './routes/Overview'
import { PlanFitsPage } from './routes/PlanFits'
import { ProductFlagsPage } from './routes/ProductFlags'
import { QuotePage } from './routes/Quote'
import { RevenuePage } from './routes/Revenue'
import { SignInPage } from './routes/SignIn'
import { logSearch, SystemPage } from './routes/System'
import { UserErasurePage } from './routes/UserErasure'
import { ViewAsPage } from './routes/ViewAs'
import { WebhooksPage } from './routes/Webhooks'

export interface RouterContext {
  queryClient: QueryClient
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFoundPage,
})

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  validateSearch: (s: Record<string, unknown>): { return_to?: string } => {
    const back = safeReturnTo(s.return_to)
    return back ? { return_to: back } : {}
  },
  component: SignInPage,
})

const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: async ({ context, location }) => {
    try {
      return { me: await context.queryClient.ensureQueryData(meQuery) }
    } catch (error) {
      // 401: signed out or past 12 hours; 403: not staff or no MFA. Sign-in shows which.
      if (isApiError(error, 401) || isApiError(error, 403))
        throw redirect({ to: '/sign-in', search: { return_to: location.href } })
      throw error
    }
  },
  component: Shell,
})

// Any other path, signed in: the not-found page inside the shell (admin-d.md F, APS-481).
const notFoundRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '$',
  component: NotFoundPage,
})

const indexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/overview' })
  },
})

const orgsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/orgs',
  validateSearch: (s: Record<string, unknown>): { q?: string } =>
    typeof s.q === 'string' && s.q ? { q: s.q.slice(0, 200) } : {},
  component: OrganizationsPage,
})

const orgRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/orgs/$orgId',
  component: OrganizationPage,
})

const viewRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/orgs/$orgId/view',
  component: ViewAsPage,
})

const userErasureRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/users/$userId/erase',
  component: UserErasurePage,
})

const erasuresRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/erasures',
  validateSearch: (s: Record<string, unknown>): { status?: ErasureFilter } =>
    ERASURE_FILTERS.includes(s.status as ErasureFilter) && s.status !== 'open'
      ? { status: s.status as ErasureFilter }
      : {},
  component: ErasuresPage,
})

const auditRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/audit',
  component: AuditPage,
})

const abuseRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/abuse',
  component: AbusePage,
})

const healthRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/health',
  component: HealthPage,
})

const infraRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/infra',
  component: InfraPage,
})

const costModelRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/cost-model',
  component: CostModelPage,
})

const planFitRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/plan-fit',
  component: PlanFitsPage,
})

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/
const quoteRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/quote',
  // Blank, from an organization (org) or from a saved quote (quote).
  validateSearch: (s: Record<string, unknown>): { org?: string; quote?: string } => ({
    ...(typeof s.org === 'string' && UUID.test(s.org) ? { org: s.org } : {}),
    ...(typeof s.quote === 'string' && UUID.test(s.quote) ? { quote: s.quote } : {}),
  }),
  component: QuotePage,
})

const overviewRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/overview',
  component: OverviewPage,
})

const systemRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/system',
  validateSearch: logSearch,
  component: SystemPage,
})

const productFlagsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/product-flags',
  component: ProductFlagsPage,
})

const revenueRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/revenue',
  validateSearch: (s: Record<string, unknown>): { month?: string } =>
    typeof s.month === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(s.month)
      ? { month: s.month }
      : {},
  component: RevenuePage,
})

const marginRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/margin',
  validateSearch: (s: Record<string, unknown>): { month?: string } =>
    typeof s.month === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(s.month)
      ? { month: s.month }
      : {},
  component: MarginPage,
})

const webhooksRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/webhooks',
  component: WebhooksPage,
})

const nfseRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/nfse',
  component: NfsePage,
})

export const routeTree = rootRoute.addChildren([
  signInRoute,
  authedRoute.addChildren([
    indexRoute,
    orgsRoute,
    orgRoute,
    viewRoute,
    userErasureRoute,
    erasuresRoute,
    auditRoute,
    revenueRoute,
    marginRoute,
    healthRoute,
    infraRoute,
    costModelRoute,
    planFitRoute,
    quoteRoute,
    overviewRoute,
    systemRoute,
    productFlagsRoute,
    abuseRoute,
    webhooksRoute,
    nfseRoute,
    notFoundRoute,
  ]),
])

export function makeRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    scrollRestoration: true,
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof makeRouter>
  }
}
